/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountModel } from '../../../shared/models';
import { AccountsApiService, OrganisationsApiService } from '../../../shared/http';
import { BaseComponent, ConfirmPasswordComponent } from '../../../../../core/components';
import { Component, ComponentRef, ViewChild, ViewContainerRef } from '@angular/core';
import {
    DownloadService,
    KeyStoreService,
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    PGPService,
    RbacService,
    UserService,
    UtilsService,
    ValidationService
} from '../../../../../core/services';
import { ErrorResponseModel } from '../../../../../core/models';
import { KeyStoreEnum } from '../../../../../core/enums';
import { complexPasswordValidation, nameValidation } from '../../../../../core/directives/validation';
import { concatMap, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, Subscriber } from 'rxjs';

@Component({
    selector: 'app-accounts-settings',
    templateUrl: './accounts-settings.component.html',
    styleUrls: ['./accounts-settings.component.scss']
})

export class AccountsSettingsComponent extends BaseComponent {
    /**
     * Reference to modal container.
     */
    @ViewChild('modalContainer', { read: ViewContainerRef, static: true })
    public modalContainer: any;

    /**
     * Account form group.
     */
    public accountForm: FormGroup;

    /**
     * Stored current user details.
     */
    public currentUserDetails: AccountModel;

    /**
     * Store the current user ID.
     */
    public currentUserId: string;

    /**
     * Stored the current Org ID for api patch.
     */
    public currentOrgID: number;

    /**
     * Store list of member for de-duping email addresses.
     */
    public listOfMembers: AccountModel[] = [];

    /**
     * Store the active step.
     */
    public activeStep = 1;

    /**
     * Avatar config.
     */
    public avatar: any = {
        color: '',
        initials: ''
    };

    /**
     * Has the user updated their password.
     */
    public passwordTouched = false;

    /**
     * Filename for key.
     */
    public privateKeyName = 'lbUserPrivate.key';

    /**
     * Disabled phone number message.
     */
    public phoneNumberDisabledMessage = null;

    /**
     * The object to submit to the API.
     * @private
     */
    private userObject: any;

    /**
     * Modal Component Reference
     * @private
     */
    private componentReference: ComponentRef<any>;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private accountsApiService: AccountsApiService,
        private downloadService: DownloadService,
        private formBuilder: FormBuilder,
        private keyStoreService: KeyStoreService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private organisationApiService: OrganisationsApiService,
        private pgpService: PGPService,
        private rbacService: RbacService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setUpApiSubscribers();
        this.setupModalSubscriber();
        this.registerButtons();
        this.checkSMS2FA();
    }

    /**
     * Validate data group.
     * @param dataGroup
     * @private
     */
    private static dataGroupIsValid(dataGroup: any): any {
        return !!dataGroup && Object.keys(dataGroup).length;
    }

    /**
     * Go to the next step to download the new key.
     */
    public nextStep(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.openConfirmPasswordModal()
                .pipe(concatMap(password => this.reEncryptKeys(password)))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: any): any => {
                        this.logService.error('AccountSettingsComponent: NextStep', error);
                        observer.next(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Re-encrypt private and organisation keys.
     * @param currentPassword
     * @private
     */
    private reEncryptKeys(currentPassword: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const newPassword = this.accountForm.get('step1.password').value;
            const hashPassword = this.pgpService.shaPassword(newPassword);
            const hashRemainder = this.pgpService.getHashRemainder(hashPassword);
            const userPrivateKey = this.keyStoreService.userPrivateKey;
            const orgPrivateKey = this.keyStoreService.orgPrivateKey;

            this.decryptUserKey(currentPassword, userPrivateKey)
                .pipe(
                    concatMap(
                        decryptedUserKey =>
                            this.decryptOrgKey(currentPassword, userPrivateKey, orgPrivateKey, decryptedUserKey)),
                    concatMap(
                        ({ decryptedUserKey, decryptedOrgKey }) =>
                            this.encryptUserKey(hashPassword, decryptedUserKey, decryptedOrgKey)),
                    concatMap(
                        ({ decryptedUserKey, decryptedOrgKey, encryptedUserKey }) =>
                            this.encryptOrgKey(decryptedUserKey, decryptedOrgKey, encryptedUserKey)),
                )
                .subscribe({
                    next: ({ encryptedUserKey, encryptedOrgKey }): void => {
                        this.userObject = {
                            privateKey: encryptedUserKey.armor(),
                            orgPrivateKey: encryptedOrgKey.armor(),
                            password: hashRemainder,
                        };
                        this.keyStoreService.storeState = KeyStoreEnum.OPEN;
                        // Set our private key in our key store
                        this.keyStoreService.userPrivateKey = this.userObject.privateKey;
                        this.activeStep++;
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AccountSettingsComponent: reEncryptTest', 'Error re-encrypting keys', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Decrypt user key helper function.
     * @param password
     * @param privateKey
     * @private
     */
    private decryptUserKey(password: string, privateKey: string): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.pgpService.decryptPrivateKeyWithPassword(password, privateKey)
                .subscribe({
                    next: (decryptedUserKey: any): void => {
                        observer.next(decryptedUserKey);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AccountSettingsComponent: decryptPrivateKeyHelper', 'Error decrypting user key', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Encrypt user key helper function.
     * @param hashPassword
     * @param decryptedUserKey
     * @param decryptedOrgKey
     * @private
     */
    private encryptUserKey(hashPassword: string, decryptedUserKey: string, decryptedOrgKey: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.pgpService.encryptKeyWithPassword(hashPassword, decryptedUserKey)
                .subscribe({
                    next: (encryptedUserKey: any): void => {
                        observer.next({ decryptedUserKey, decryptedOrgKey, encryptedUserKey });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AccountSettingsComponent: encryptUserKeyHelper', 'Error encrypting user key', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Encrypt org key helper function.
     * @param decryptedUserKey
     * @param decryptedOrgKey
     * @param encryptedUserKey
     * @private
     */
    private encryptOrgKey(decryptedUserKey: {
        armor: () => string;
    }, decryptedOrgKey: string, encryptedUserKey: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.pgpService.encryptKeyWithPassword(decryptedUserKey.armor(), decryptedOrgKey)
                .subscribe({
                    next: (encryptedOrgKey: any): void => {
                        observer.next({ encryptedUserKey, encryptedOrgKey });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AccountSettingsComponent: encryptOrgKeyHelper', 'Error encrypting org key', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Decrypt org key helper function.
     * @param password
     * @param userPrivateKey
     * @param orgPrivateKey
     * @param decryptedUserKey
     * @private
     */
    private decryptOrgKey(password: any, userPrivateKey: string, orgPrivateKey: string, decryptedUserKey: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.pgpService.getDecryptedOrgKeyWithUserPassword(password, userPrivateKey, orgPrivateKey)
                .subscribe({
                    next: (decryptedOrgKey: any): void => {
                        observer.next({ decryptedUserKey, decryptedOrgKey });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AccountSettingsComponent: decryptOrgKeyHelper', 'Error decrypting org key', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set private key as true in form.
     * @decryptedKey
     */
    public downloadKeyAndValidate(decryptedKey: { armor: () => any; }): void {
        this.downloadService.generateDownload(decryptedKey.armor(), this.privateKeyName);
        this.accountForm.get('step2.keyDownloaded').setValue(true);
    }

    /**
     * Update organisation/user details.
     */
    public updateAccount(): Observable<any> {
        let accountFormData: any = this.userObject ? this.userObject : this.formatUserDetails();

        if (this.userObject && this.accountForm.get('step1.firstName').touched
            || this.accountForm.get('step1.lastName').touched
            || this.accountForm.get('step1.email').touched) {
            accountFormData = { ...this.userObject, ...this.formatUserDetails() };
        }

        return new Observable((observer: Subscriber<any>): void => {
            this.accountsApiService.update(this.currentUserId.toString(), accountFormData)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.accountForm.markAsPristine();
                        if (this.passwordTouched) {
                            this.openLogoutModal();
                        } else {
                            this.setUpApiSubscribers();
                        }
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AccountComponent: updatedAccount', 'Error submitting data', error);
                        observer.error(error);
                        observer.complete();
                    },
                });
        });
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.accountForm.get(group) as FormGroup;
    }

    /**
     * Confirm user's current password before submit.
     * @private
     */
    private openConfirmPasswordModal(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const confirmPasswordModal = this.modalContainer.createComponent(ConfirmPasswordComponent);
            confirmPasswordModal.instance.message = 'account_enter_current_password';
            confirmPasswordModal.instance.userDetails = this.currentUserDetails;
            confirmPasswordModal.instance.closeModalInternally = false;
            confirmPasswordModal.instance.callBack = (password: string) => {
                observer.next(password);
                observer.complete();
            };

            this.componentReference = confirmPasswordModal;
        });
    }

    /**
     * Setup user form.
     * @private
     */
    private setupFormGroup(): void {
        this.accountForm = this.formBuilder.group({
            step1: this.formBuilder.group({
                firstName: [null, [Validators.required, Validators.maxLength(254), nameValidation]],
                lastName: [null, [Validators.required, Validators.maxLength(254), nameValidation]],
                email: [null, [Validators.required, ValidationService.emailValidator]],
                countryCode: [null],
                phoneNumber: [null, [Validators.required, ValidationService.phoneNumberValidator]],
                password: [null, complexPasswordValidation],
                confirmPassword: [null]
            }),
            step2: this.formBuilder.group({
                keyDownloaded: [null, Validators.required]
            }),
        });
    }

    /**
     * Check email addresses are the same.
     * @private
     */
    private setupFormSubscribers(): void {
        const accountFormStep1: AbstractControl = this.accountForm.get('step1');
        const passwordInput: AbstractControl = accountFormStep1.get('password');
        const confirmPasswordInput: AbstractControl = accountFormStep1.get('confirmPassword');

        accountFormStep1.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.utilsService.debounce('account', (): void => {
                        if (data.email !== this.currentUserDetails.email && this.listOfMembers.some(item => item.email === data.email)) {
                            accountFormStep1.get('email').setErrors({
                                'duplicateEmail': `${this.languageService.translate('email_in_use')}`
                            });
                            accountFormStep1.get('email').markAsTouched();
                        }

                        if (this.utilsService.hasFormDataChanged(this.currentUserDetails, data, ['password', 'confirmPassword'])) {
                            accountFormStep1.markAsDirty();
                        } else {
                            accountFormStep1.markAsPristine();
                        }
                    }, 250);
                }
            });

        passwordInput.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (password: string): void => {
                    this.utilsService.debounce('passwordInput', (): void => {
                        this.setValidationForBothPasswordFields(password, passwordInput, confirmPasswordInput);
                    }, 500);
                }
            });

        confirmPasswordInput.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (confirmPassword: string): void => {
                    // Don't use debounce here as it will cause the submit button to flash.
                    const password: any = accountFormStep1.get('password').value;
                    this.utilsService.validatePasswordWithConfirmPassword(password, confirmPassword, confirmPasswordInput);
                }
            });
    }

    /**
     * Set validation for both password fields
     * @param password
     * @param passwordInput
     * @param confirmPasswordInput
     * @private
     */
    private setValidationForBothPasswordFields(
        password: string,
        passwordInput: AbstractControl,
        confirmPasswordInput: AbstractControl
    ): void {
        if (password && password.length) {
            passwordInput.markAsTouched();
            this.passwordTouched = true;
            if (passwordInput.valid) {
                confirmPasswordInput.enable();
                confirmPasswordInput.addValidators(Validators.required);
                confirmPasswordInput.updateValueAndValidity({ emitEvent: false });
            }
        } else {
            this.passwordTouched = false;
            confirmPasswordInput.disable();
            confirmPasswordInput.setErrors({ 'confirmMismatch': null });
        }
    }

    /**
     * Get data from APIs.
     * @private
     */
    private setUpApiSubscribers(): void {
        const user: any = this.utilsService.duplicateObject(this.userService.getUser());
        this.currentUserId = user.id;
        this.currentOrgID = user.organisationId;

        const apiCalls = {
            listOfMembers: this.organisationApiService.getMembers(this.currentOrgID.toString()),
            currentUserDetails: this.accountsApiService.get(this.currentUserId),
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                dataGroup => {
                    if (AccountsSettingsComponent.dataGroupIsValid(dataGroup['listOfMembers'])) {
                        this.listOfMembers = dataGroup['listOfMembers'];
                    }

                    if (AccountsSettingsComponent.dataGroupIsValid(dataGroup['currentUserDetails'])) {
                        this.currentUserDetails = dataGroup['currentUserDetails'];
                    }

                    this.setAvatar();
                    this.patchDataToForms();
                });
    }

    /**
     * Set the avatar class variable.
     * @private
     */
    private setAvatar(): void {
        this.avatar = this.userService.createUserAvatarFromNames(this.currentUserDetails.firstName, this.currentUserDetails.lastName);
    }

    /**
     * Patch API data to form.
     * @private
     */
    private patchDataToForms(): void {
        const { firstName, lastName, phoneNumber, countryCode, email } = this.currentUserDetails;
        const accountDetails = {};
        accountDetails['firstName'] = firstName;
        accountDetails['lastName'] = lastName;
        accountDetails['phoneNumber'] = this.utilsService.cleanPhoneNumber(phoneNumber, countryCode);
        if (countryCode) {
            accountDetails['countryCode'] = countryCode;
        }

        accountDetails['email'] = email;
        accountDetails['password'] = null;
        accountDetails['confirmPassword'] = null;

        this.accountForm.get('step1').patchValue(accountDetails);
    }

    /**
     * Remove unneeded properties from form before submission
     * @private
     */
    private formatUserDetails(): any {
        const accountForm: any = this.accountForm.value.step1;

        delete accountForm.password;
        delete accountForm.confirmPassword;

        return accountForm;
    }

    /**
     * Open logout modal.
     * @private
     */
    private openLogoutModal(): void {
        this.modalService.openModalInParent('logoutCountdown');
    }

    /**
     * Setup modal subscriber
     * @private
     */
    private setupModalSubscriber(): void {
        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                modal => {
                    // Destroy our modal component with reference.
                    if (modal.modalId === 'confirm-password') {
                        this.componentReference.destroy();
                        this.loadingService.loadingButtonEmitter.next(false);
                    }
                });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'update-settings': [
                { path: '/api/v1/accounts/:id', methods: ['PATCH'] }
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    /**
     * If SMS 2FA is enabled disable the phone number input.
     * @private
     */
    private checkSMS2FA(): void {
        const tfaType = this.userService.getUser('tfaType');
        if (tfaType === 'SMS') {
            this.accountForm.get('step1.countryCode').disable();
            this.accountForm.get('step1.phoneNumber').disable();
            this.phoneNumberDisabledMessage = 'accounts_settings_phone_number_disabled';
        } else {
            this.accountForm.get('step1.countryCode').enable();
            this.accountForm.get('step1.phoneNumber').enable();
            this.phoneNumberDisabledMessage = null;
        }
    }
}
